/**
 * The module registry — one row per screen, and the single source for the nav, the route tree,
 * the ⌘K palette and the breadcrumb.
 *
 * Three things that would otherwise drift are pinned together here: a screen cannot exist without
 * a nav entry, a nav entry cannot point at a route that is not registered, and the scope that
 * gates the route is the same scope that decides whether the nav item renders at all.
 *
 * Paths are readable and section-shaped (`/money/payouts`), not key-shaped. An operator pastes
 * one into a colleague's chat window and it has to say what it is.
 */
import type { IconName } from '../components/icons';
import { SCOPES, type Scope } from '../lib/rbac';

export interface AdminModule {
  key: string;
  path: string;
  /** Nav label — short. `title` is the page heading and may be longer. */
  label: string;
  title: string;
  description: string;
  icon: IconName;
  group: string;
  /** Who may reach it. `superadmin` passes everything without being listed. */
  scopes: readonly Scope[];
  /** Reached from another screen, never from the nav — a detail route. */
  hidden?: boolean;
  /** Rendered without the shell chrome. Only sign-in and invitation acceptance are. */
  bare?: boolean;
  /**
   * **Whether this screen is talking to the API, and if not, what it is waiting for.**
   *
   * Required, and deliberately so: a new screen cannot be added without saying where its data
   * comes from. A console that renders invented numbers without saying they are invented is worse
   * than one that renders nothing — somebody eventually acts on them, and nothing on the screen
   * ever told them not to.
   */
  wiring: Wiring;
}

export type Wiring =
  /** Every value on the screen came from the API. */
  | { state: 'live' }
  /** Some of it did. `note` says which part, because "live" would over-claim. */
  | { state: 'partial'; note: string }
  /** None of it did. `slice` is the backend slice in `docs/13-execution/05` that unblocks it. */
  | { state: 'fixtures'; slice: string; what: string };

/** Everyone with any scope at all can see it — the dashboard, your own account. */
const ANY: readonly Scope[] = SCOPES;

export const MODULES: readonly AdminModule[] = [
  {
    key: 'dashboard',
    path: '/',
    label: 'Dashboard',
    title: 'Dashboard',
    description: 'What needs a human in the next three hours — not a BI report.',
    icon: 'layout-dashboard',
    group: 'Overview',
    scopes: ANY,
    /* `live` since the thin-coverage tile read E36's grid: every tile now has a read behind it.
       There is deliberately no rupee-sum payable figure — /v1/admin/payables is a paged list and
       the sum of one page is not the book — and the money and coverage tiles render only for the
       scopes their endpoints admit. */
    wiring: { state: 'live' },
  },

  {
    key: 'funnel',
    path: '/analytics/funnel',
    label: 'Funnel',
    title: 'Booking funnel',
    description:
      'Eight steps from first view to review · where people leave, and which two steps the server vouches for',
    icon: 'filter',
    group: 'Analytics',
    // `growth` alone: the backend is require_scope("growth") per doc 43 §4.2, and `can()`
    // already implies superadmin. Listing ops_dispatch here would offer a nav item that 403s.
    scopes: ['growth'],
    wiring: { state: 'live' },
  },
  {
    key: 'metrics',
    path: '/analytics/metrics',
    label: 'Metric explorer',
    title: 'Metric explorer',
    description:
      'Any rollup key, one grain at a time · the catalogue comes from the server, never a local list',
    icon: 'chart-no-axes-column',
    group: 'Analytics',
    scopes: ['growth'],
    wiring: { state: 'live' },
  },
  {
    key: 'health',
    path: '/analytics/dispatch-health',
    label: 'Dispatch health',
    title: 'Dispatch health',
    description: 'Worst city first — this screen exists to show trouble, not health',
    icon: 'activity',
    group: 'Analytics',
    scopes: ['ops_dispatch'],
    wiring: { state: 'live' },
  },
  {
    key: 'supply',
    path: '/analytics/coverage',
    label: 'Coverage & liquidity',
    title: 'Coverage & liquidity',
    description: 'Where supply is thin, and how fast requests are taken, by puja and city',
    icon: 'construction',
    group: 'Analytics',
    scopes: ['supply_kyc', 'ops_dispatch'],
    /* ⛔ `live` since E50 wrote the `supply.*` keys. This screen was deferred on TWO reads and
       both have now landed: G4's coverage grid with E36, G5's liquidity keys with E50. Checked
       rather than assumed — the screen imports no fixtures, and the wiring note named G5 as the
       last outstanding one. */
    wiring: { state: 'live' },
  },

  {
    key: 'dispatch',
    path: '/ops/dispatch',
    label: 'Dispatch board',
    title: 'Dispatch board',
    description: 'Every booking by state and ceremony time · escalations first',
    icon: 'calendar-clock',
    group: 'Bookings',
    scopes: ['ops_dispatch'],
    /* `live`: bookings, their snapshots, the full trail and the superadmin override. Wave history
       and manual matching moved to the matching console (B23) and replacements have their own
       queue (B24); the footer says so rather than this note. */
    wiring: { state: 'live' },
  },
  {
    key: 'replacements',
    path: '/ops/replacements',
    label: 'Replacements',
    title: 'Replacement queue',
    description: 'Bookings that lost their pandit · ordered by time to the ceremony',
    icon: 'repeat',
    group: 'Bookings',
    scopes: ['ops_dispatch'],
    /* `live`: the queue, the cause, the standby state and all three waterfall steps — promote,
       re-dispatch, give up. A pandit's own cancellation happens in their app, and removing
       somebody for cause lives on the matching console beside the assignment it acts on: by the
       time a booking reaches this queue there is nothing left to pull. */
    wiring: { state: 'live' },
  },
  {
    key: 'matching',
    path: '/ops/matching',
    label: 'Matching console',
    title: 'Matching console',
    description: 'Every wave that went out, and the two ways ops can push one along',
    icon: 'git-branch',
    group: 'Bookings',
    scopes: ['ops_dispatch'],
    /* ⛔ `live` since E52 closed CSR-031 — and this note's own wording is why it could flip:
       the exclusion diagnostic was the ONE thing missing, checked rather than assumed (the screen
       imports no fixtures at all). Coverage & liquidity, by contrast, is deferred on two reads and
       stays on fixtures. Clearing one blocker only makes a screen honest when it was the last. */
    /* ⛔ No `note` — the type forbids one on a `live` screen, and that is the registry enforcing
       the same rule by hand: a note exists to name the half that is NOT real, so a live screen
       with a caveat is either mistyped or not actually live. */
    wiring: { state: 'live' },
  },
  {
    key: 'conflicts',
    path: '/ops/conflicts',
    label: 'Conflicts & slots',
    title: 'Conflicts, blackouts & manual slots',
    description: 'Calendar collisions, closed dates, and one-off slots',
    icon: 'calendar-x',
    group: 'Bookings',
    scopes: ['ops_dispatch'],
    /* `live`: the conflicts queue, blackouts and manual slots. A conflict has no 'resolve' action
       because none exists — it clears when the pandit reopens the time, ops lifts the blackout, or
       the booking moves (B22). */
    wiring: { state: 'live' },
  },

  {
    key: 'kyc',
    path: '/supply/kyc',
    label: 'KYC queue',
    title: 'Pandit KYC queue',
    description: 'Cases in review, oldest first · six badges decided independently',
    icon: 'file-check',
    group: 'Supply',
    scopes: ['supply_kyc'],
    // `live`: every part of the case — documents, references, checks, badges, decision — is wired (CSR-026, E01).
    wiring: { state: 'live' },
  },
  {
    key: 'pandits',
    path: '/supply/pandits',
    label: 'Pandit directory',
    title: 'Pandit directory',
    description: 'The roster, the activation gate, and which pujas each pandit may be sent for.',
    icon: 'users',
    group: 'Supply',
    scopes: ['supply_kyc'],
    /* `live`: roster, lifecycle, listing, skill confirmation, and the Quality tab (E43) — score
       tiles, trend, open flags, and issuing and revoking quality actions behind typed phrases. A
       suspension leaves matching and search on the server's own predicate; `training_required` is
       recorded but not yet enforced, which the issue dialog says rather than this note. */
    wiring: { state: 'live' },
  },
  {
    key: 'coverage',
    path: '/supply/coverage',
    label: 'Skills coverage',
    title: 'Skills coverage',
    description: 'Which puja × city pairs have nobody to send — the recruiting list',
    icon: 'grid-3x3',
    group: 'Supply',
    scopes: ['supply_kyc'],
    wiring: { state: 'live' },
  },

  {
    key: 'tickets',
    path: '/support/tickets',
    label: 'Tickets',
    title: 'Support tickets',
    description: 'Ordered by the pinned SLA clock · a safety ticket is urgent by category',
    icon: 'life-buoy',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'disputes',
    path: '/support/disputes',
    label: 'Disputes',
    title: 'Disputes & escalations',
    description: 'Opening one holds the pandit’s payout · resolving needs a decision, not a status',
    icon: 'gavel',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'reviews',
    path: '/support/reviews',
    label: 'Reviews & quality',
    title: 'Reviews & quality actions',
    description: 'Moderation, redaction and the flags supply consumes',
    icon: 'star',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'commspool',
    path: '/support/numbers',
    label: 'Call numbers',
    title: 'Masked calling numbers',
    description: 'The platform numbers calls are bridged through — and how many are free',
    icon: 'phone',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'sla',
    path: '/support/sla',
    label: 'SLA policies',
    title: 'SLA policies',
    description: 'Versioned and pinned at ticket creation — editing never moves a live clock',
    icon: 'timer',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'macros',
    path: '/support/macros',
    label: 'Reply macros',
    title: 'Reply macros',
    description: 'M1–M12 in English and Hindi · placeholders and the content lint checked on save',
    icon: 'scroll-text',
    group: 'Support',
    scopes: ['support'],
    wiring: { state: 'live' },
  },

  {
    key: 'users',
    path: '/people/users',
    label: 'Users & accounts',
    title: 'Users & accounts',
    description: 'Devotee accounts · masked by default · a reveal is an audited, reasoned act',
    icon: 'user-round',
    group: 'People',
    /* Growth reaches the aggregate strip and nothing else — the screen itself refuses the
       drill-down. Segments, not people. */
    scopes: ['support', 'growth'],
    wiring: { state: 'live' },
  },
  {
    key: 'dsr',
    path: '/people/users/$userId/dsr',
    label: 'DSR workspace',
    title: 'Data-subject request workspace',
    description:
      'Assisted export, correction and closure · the pipeline verifies identity, not the operator',
    icon: 'shield',
    group: 'People',
    scopes: ['support'],
    /* Reached from an account, never from the nav: a DSR is always about one person. */
    hidden: true,
    wiring: { state: 'live' },
  },

  {
    key: 'catalogue',
    path: '/catalogue/pujas',
    label: 'Puja catalogue',
    title: 'Puja catalogue',
    description: 'Every puja · occasions, deities, sampradaya, FAQs and media in one record',
    icon: 'book-open',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    wiring: {
      state: 'partial',
      note: 'List, detail, copy editing, activate/deactivate, archive and restore are live, and so are all nine list editors: steps, FAQs and service modes, plus deities, occasions and traditions as pickers over the taxonomy read, the related rail and the media set picked by storage key, and the participant matrix. Every one is a replace-the-whole-list PUT staged until Save, because a toggle that wrote on click would let one mis-tap send a set with a member missing. Per-city offerings and price overrides still live on the geo screen (E41).',
    },
  },
  {
    key: 'search',
    path: '/catalogue/search',
    label: 'Search dictionary',
    title: 'Search synonyms, aliases & misses',
    description: 'The words a devotee uses for a thing we call something else',
    icon: 'search',
    group: 'Catalogue',
    /* The READ scopes, matching the server: "what did people search for and find nothing" is a
       question growth and support both ask. Every write is `catalogue_content` alone, gated on
       the buttons — nobody is shown a door the API will not open. */
    scopes: ['catalogue_content', 'growth', 'support'],
    wiring: { state: 'live' },
  },
  {
    key: 'taxonomy',
    path: '/catalogue/taxonomy',
    label: 'Categories & axes',
    title: 'Categories & taxonomy',
    description: 'How the catalogue is grouped, and the four axes every puja is classified on',
    icon: 'layers',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    /* `live`: categories and all four axes are fully managed (CSR-028, E02). Occasions are
       creation-closed on purpose — `occasion.code` is a URL contract — and the home tile grid is
       gone because it had no endpoint at any path. */
    wiring: { state: 'live' },
  },
  {
    key: 'ritualschools',
    path: '/catalogue/ritual-schools',
    label: 'Ritual schools',
    title: 'Ritual schools',
    description:
      'The shakhas, sutras and paddhatis pandits follow · a tree that cannot loop · switched off never deleted',
    icon: 'book-open',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'samagri',
    path: '/catalogue/samagri',
    label: 'Samagri',
    title: 'Samagri items & kits',
    description:
      'What each puja uses and the kits it is packed in · costs dated · kit totals worked out by the server',
    icon: 'package',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'pricing',
    path: '/catalogue/pricing',
    label: 'Packages & pricing',
    title: 'Packages & pricing',
    description: 'Package tiers, versioned price sheets and fee rules — integer paise only',
    icon: 'tag',
    group: 'Catalogue',
    /* Catalogue owns the packages; finance owns what may be charged for them. Both read the
       screen, and publishing a version is gated on finance inside it. */
    scopes: ['catalogue_content', 'finance'],
    /* Tiers, price versions, the per-city preview, per-city overrides, publish and discard. A
       tier is created, described and retired on the puja catalogue's Tiers tab; travel-zone
       charge rules are the city screen's. */
    wiring: { state: 'live' },
  },
  {
    key: 'temples',
    path: '/catalogue/temples',
    label: 'Temples',
    title: 'Temples & authorization',
    description: 'The directory, the signed agreements, and the renewal board',
    icon: 'building',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    /* `live`: the directory, agreements, transitions, the signed-paper upload and download, the
       renewal board, publishing, the editorial surface and imagery (presign → PUT → confirm, then
       alt text, visibility and removal). Nothing on the editorial form can assert the
       official-wording claim; that is derived from the agreements on every read. */
    wiring: { state: 'live' },
  },
  {
    key: 'geo',
    path: '/catalogue/geo',
    label: 'Cities & areas',
    title: 'Cities & service areas',
    description: 'Serviceable cities · areas, travel zones and what each city may offer',
    icon: 'map-pin',
    group: 'Catalogue',
    scopes: ['catalogue_content'],
    wiring: {
      state: 'live',
    },
  },

  {
    key: 'payments',
    path: '/money/payments',
    label: 'Payments',
    title: 'Payments & transactions',
    description: 'Gateway mirror · search, webhook history and idempotent replay',
    icon: 'credit-card',
    group: 'Money',
    // Three scopes, matching what the endpoint actually admits. Dispatch and support read
    // payment status for bookings in their queues and get a reduced projection — amounts and
    // status, never the instrument (admin-14 §1 footnote 4). Listing only `finance` here would
    // hide a screen the server would happily serve them.
    scopes: ['finance', 'ops_dispatch', 'support'],
    /* The browser, the drawer timeline, the ledger tab and the 24-hour health strip read
       /v1/admin/payments and /v1/admin/bookings/{id}/ledger, with instruments withheld from every
       scope but finance. Webhook replay has its own screen; refund initiation is on the refunds
       screen, by BMP code. */
    wiring: { state: 'live' },
  },
  {
    key: 'webhooks',
    path: '/money/webhooks',
    label: 'Webhooks',
    title: 'Webhook console',
    description: 'Every callback Razorpay sent us, what it caused, and whether we refused it',
    icon: 'webhook',
    group: 'Money',
    // Read by everyone who fields "I paid and it says otherwise" — replaying is finance's, and
    // the server enforces that separately.
    scopes: ['finance', 'ops_dispatch', 'support'],
    wiring: { state: 'live' },
  },
  {
    key: 'risk',
    path: '/money/risk',
    label: 'Risk',
    title: 'Fraud review',
    description: 'The risk review queue · cases, the rules that fired, and the rules themselves',
    icon: 'shield',
    group: 'Money',
    scopes: ['finance'],
    wiring: { state: 'live' },
  },
  {
    key: 'ledger',
    path: '/money/ledger',
    label: 'Finance ledger',
    title: 'Finance ledger',
    description: 'Read-only · the journal, the trial balance, and the chart of accounts',
    icon: 'scale',
    group: 'Money',
    scopes: ['finance'],
    wiring: { state: 'live' },
  },
  {
    key: 'ledgertxn',
    path: '/money/ledger/$txnId',
    label: 'Transaction detail',
    title: 'Transaction detail',
    description: 'The correction chain, the context, and the one action that is allowed',
    icon: 'scale',
    group: 'Money',
    /* Ops and support reach the reduced projection — the booking's money story without the chart
       of accounts. The screen itself withholds the journal legs. */
    scopes: ['finance', 'ops_dispatch', 'support'],
    hidden: true,
    wiring: { state: 'live' },
  },
  {
    key: 'close',
    path: '/money/close',
    label: 'Recon & close',
    title: 'Reconciliation & month close',
    description: 'Daily breaks, the checklist, and the sign-off that locks the month',
    icon: 'calendar-check',
    group: 'Money',
    scopes: ['finance'],
    // The ledger half of B29 has landed; the reconciliation half has not. `recon_run` and
    // `recon_break` have no ratified DDL in `docs/03-data/` at all — the payments doc that
    // specifies them says so itself and asks for one (CSR-035). Until they exist there is
    // nothing honest for this screen to read.
    /* `partial`, not `live`: the checklist, the break queue and the sign-off are real, and five
       of the six nightly passes report `failed` because no provider recon fetcher or bank feed
       exists yet (E30's named sub-blocker). The screen shows those refusals verbatim rather than
       hiding them, which is the honest state — but "live" would over-claim a reconciliation that
       currently compares nothing against the gateway. */
    wiring: {
      state: 'partial',
      note: 'The checklist, break queue and four-eyes sign-off are live. Five of the six nightly passes report `failed` — no provider recon fetcher or bank import exists yet, so nothing is compared against the gateway.',
    },
  },
  {
    key: 'payouts',
    path: '/money/payouts',
    label: 'Payouts',
    title: 'Payouts & settlement release',
    description: 'The payable board and the payout runs · four-eyes above the ceiling · the pandit sees the same breakdown',
    icon: 'banknote',
    group: 'Money',
    scopes: ['finance'],
    wiring: {
      state: 'partial',
      note: 'The payable board and payout runs are live — preview, build, approve, reject and recording an out-of-band NEFT all hit the API. Execute is live too and refuses: no payout provider is wired yet, and the screen shows that refusal rather than dressing it as a queued transfer.',
    },
  },
  {
    key: 'approvals',
    path: '/money/approvals',
    label: 'Four-eyes queue',
    title: 'Four-eyes approvals',
    description: 'Payout runs waiting for a second pair of eyes · no self-approval',
    icon: 'user-check',
    group: 'Money',
    scopes: ['finance'],
    wiring: {
      state: 'partial',
      /* ⛔ Narrowed from four to three: settings four-eyes SHIPPED (CSR-022) and renders on the
         Settings screen, beside the setting it changes. This note claimed otherwise for a while,
         which is how a built thing gets built twice — the register lagged the contract. */
      note: 'Payout runs are live: the queue, the figures inside a run, approve and reject all hit the API, and the server refuses self-approval independently. Settings approvals are live too, on the Settings screen beside the setting they change, with a pointer from here. Refund corrections are live on the Refunds screen, and this queue points there with their count. Tax writes are live here too: a rate version, a TDS deposit or a GST remittance recorded on the Tax screen waits in this queue until a different finance admin approves it, and the requester can only withdraw their own. Write-offs (blocked on E19) have no four-eyes endpoint yet and are absent from the queue rather than mocked into it.',
    },
  },
  {
    key: 'refunds',
    path: '/money/refunds',
    label: 'Refunds',
    title: 'Refunds & reversals',
    description: 'Component lines, not lump sums · and the gateway’s word on what has paid',
    icon: 'undo-2',
    group: 'Money',
    // Support reads: "has her refund gone out?" is most of what they are asked. Raising one is
    // finance's, and the server enforces that separately rather than the nav guessing.
    scopes: ['finance', 'support'],
    wiring: { state: 'live' },
  },
  {
    key: 'policies',
    path: '/money/cancellation-policies',
    label: 'Cancellation policies',
    title: 'Cancellation policies',
    description: 'Versioned refund windows · bookings keep the version they pinned',
    icon: 'scale',
    group: 'Money',
    // Built from doc 05-admin/17 §2.5 by the owner's 2026-10-26 ruling (no design). Support
    // reads the windows to answer "what will I get back?"; only finance writes, server-enforced.
    scopes: ['finance', 'support'],
    wiring: { state: 'live' },
  },
  {
    key: 'tax',
    path: '/money/tax',
    label: 'Tax & compliance',
    title: 'Tax & compliance',
    description: 'Rate versions, the 194-O register, challans, certificates and GST',
    icon: 'receipt-indian-rupee',
    group: 'Money',
    scopes: ['finance'],
    wiring: {
      state: 'partial',
      note: 'The rate registry, the 194-O register, deposits, certificate runs and the GST register are all live, and the return exports as the CSV a CA files from. The Documents tab carries the gap audit, the invoice register, the GSTR-1 export, the re-render and four-eyes credit notes (doc 04-backend/30, built from the spec). The certificate PDF itself is rendered by the quarterly job (B45), so nothing here offers a download until the file exists — and Section 52 TCS reads as switched off because it is, pending the accountant.',
    },
  },

  {
    key: 'cms',
    path: '/content/cms',
    label: 'CMS & SEO',
    title: 'CMS & SEO page builder',
    description: 'Every heading, paragraph and FAQ the public site shows — as rows',
    icon: 'globe',
    group: 'Content',
    scopes: ['catalogue_content'],
    /* `live` since the draft preview landed (CSR-041): it was the last half-wired piece. */
    wiring: { state: 'live' },
  },
  {
    key: 'notifhelp',
    path: '/content/notification-help',
    label: 'Notifications help',
    title: 'Notifications help',
    description: 'Per-maker steps a pandit follows when their phone silences booking alerts',
    icon: 'bell',
    group: 'Content',
    scopes: ['catalogue_content'],
    /* Built from the docs under the 2026-10-26 ruling; no design exists (CSR-109). */
    wiring: { state: 'live' },
  },
  {
    key: 'redirects',
    path: '/content/redirects',
    label: 'Redirects',
    title: 'Redirects',
    description:
      '301, 302 and 410 · chains refused in both directions, and nothing is ever deleted',
    icon: 'signpost',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'seoregistry',
    path: '/content/seo/registry',
    label: 'Page registry',
    title: 'Page registry & budget',
    description:
      'Every public URL and its tier · prerendered against the deploy file budget, pinned and audited',
    icon: 'globe',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'seotemplates',
    path: '/content/seo/templates',
    label: 'SEO templates',
    title: 'SEO templates',
    description:
      'The rows every title, description and H1 renders from · tokens checked at save, never switched off under a live page',
    icon: 'file-text',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'seobudget',
    path: '/content/seo/budget',
    label: 'Page budget',
    title: 'Page budget',
    description:
      'Deploy files against the budget · the last build, flip history and tiers by page type',
    icon: 'chart-no-axes-column',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'labels',
    path: '/content/labels',
    label: 'Labels',
    title: 'Labels',
    description:
      'The English and Hindi words each code is shown as · linted, audited, switched off never deleted',
    icon: 'globe',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },
  {
    key: 'media',
    path: '/content/media',
    label: 'Media library',
    title: 'Media library',
    description: 'Four fixed ratios · scanned on upload · no text ever baked into an image',
    icon: 'image',
    group: 'Content',
    scopes: ['catalogue_content'],
    wiring: { state: 'live' },
  },

  {
    key: 'offers',
    path: '/growth/offers',
    label: 'Offers & referrals',
    title: 'Offers, referrals & Punya',
    description: 'Budget caps are hard stops · referral rulings are reasoned and audited',
    icon: 'percent',
    group: 'Growth',
    scopes: ['growth'],
    wiring: { state: 'live' },
  },
  {
    key: 'campaigns',
    path: '/growth/campaigns',
    label: 'Campaigns',
    title: 'Notifications & campaigns',
    description: 'Templates in both locales, quiet hours, caps, and delivery forensics',
    icon: 'megaphone',
    group: 'Growth',
    scopes: ['growth'],
    // Templates with their channel gates, quiet hours, delivery forensics, the read-only event
    // catalogue and the campaign builder (two-figure audience, test-send before send, typed reach).
    wiring: { state: 'live' },
  },

  {
    key: 'settings',
    path: '/platform/settings',
    label: 'Settings registry',
    title: 'Settings registry',
    description: 'Typed settings by domain · defaults, revisions, four-eyes keys and a diff before every save',
    icon: 'sliders-horizontal',
    group: 'Platform',
    scopes: ['superadmin'],
    wiring: { state: 'live' },
  },
  {
    key: 'flags',
    path: '/platform/flags',
    label: 'Feature flags',
    title: 'Feature flags',
    // Everyone reads — a flag nobody but the founder can see is a flag nobody can debug.
    // Who may FLIP each one is the flag's own `editable_by`, enforced server-side per row.
    description: 'Everyone reads; each flag names the scope that may flip it, and the server checks',
    icon: 'toggle-left',
    group: 'Platform',
    scopes: ANY,
    wiring: { state: 'live' },
  },
  {
    key: 'abuse',
    path: '/platform/abuse',
    label: 'Abuse controls',
    title: 'Abuse controls',
    description: 'Block-lists, and what one caller is currently up against',
    icon: 'shield',
    group: 'Platform',
    // `support` and superadmin, mirroring the server. The people who meet a wrongly-blocked
    // devotee are the support desk; an unblock that needs a founder waits until Monday. The
    // compensating control is that every mutation is audited with a mandatory reason.
    scopes: ['support'],
    wiring: { state: 'live' },
  },
  {
    key: 'audit',
    path: '/platform/audit',
    label: 'Audit & jobs',
    title: 'Audit log and job health',
    description: 'Append-only event stream, hash-chain verification, month digests, job health',
    icon: 'scroll-text',
    group: 'Platform',
    scopes: ['superadmin'],
    /* The Jobs tab is back, and this time it renders only what `job_run` holds. It was removed
       once for showing four hard-coded rows — including a `settlement.reconcile` that "Succeeded
       with breaks" for a job that did not exist — because invented operational history about
       money is the worst thing this console can render. The label, title and description went
       with it, since a nav entry naming a tab you cannot find is its own small lie; `job_run`
       (doc 45 §3) and its read (§4.2) have landed, so all four return together.
       ⛔ A job that has never run renders as "Never run", not as healthy and not as absent —
       that is the question the panel exists to answer. */
    wiring: { state: 'live' },
  },
  {
    key: 'admins',
    path: '/platform/admins',
    label: 'Admin accounts',
    title: 'Admin accounts',
    description: 'Invitation-only · seven scopes · a new admin starts with none',
    icon: 'shield-user',
    group: 'Platform',
    /* Reachable by anyone so the screen can give the *specific* refusal — that admin and scope
       management sits deliberately outside the RBAC matrix — rather than the shell's generic one.
       The roster, the matrix and the policy stay hidden behind the superadmin check inside. */
    scopes: ANY,
    wiring: { state: 'live' },
  },

  {
    key: 'invite',
    path: '/invite',
    label: 'Accept invitation',
    title: 'Accept invitation',
    description: 'Set the password on an account a superadmin created — the only way one is finished',
    icon: 'circle-user',
    group: 'You',
    scopes: ANY,
    hidden: true,
    bare: true,
    wiring: { state: 'live' },
  },
  {
    key: 'login',
    path: '/login',
    label: 'Sign in',
    title: 'Sign in',
    description: 'The console’s one brand moment — and the only page outside the shell',
    icon: 'circle-user',
    group: 'You',
    scopes: ANY,
    hidden: true,
    /* Rendered without the nav, the top bar or the page header: a sign-in page inside the console
       chrome would show a logged-out visitor the map of everything they cannot reach. */
    bare: true,
    wiring: { state: 'live' },
  },
  {
    key: 'account',
    path: '/account',
    label: 'My account',
    /* Not "Settings": Platform → Settings registry is the platform's settings, and two screens
       whose heading is the same word send the wrong one to whoever is in a hurry. */
    title: 'Your account',
    description: 'Manage your own account, security, notifications and appearance',
    icon: 'circle-user',
    group: 'You',
    scopes: ANY,
    /* `live`: E35 closed G11, G13 and G14 and E59 closed the last three — the two display toggles
       persist on the account (`PATCH /v1/admin/me`), recovery codes are counted and regenerated
       behind a two-factor step-up, and a deactivation request lands in `admin_deactivation_request`
       where a superadmin sees it on the roster. Every control on the screen now does what it says. */
    wiring: { state: 'live' },
  },
];

/** Nav order. Derived from the registry, so a new module cannot be missing from the nav. */
export const GROUP_ORDER: readonly string[] = [
  'Overview',
  'Analytics',
  'Bookings',
  'Supply',
  'Support',
  'People',
  'Catalogue',
  'Money',
  'Content',
  'Growth',
  'Platform',
  'You',
];

export const MODULE_BY_KEY: Readonly<Record<string, AdminModule>> = Object.fromEntries(
  MODULES.map((m) => [m.key, m]),
);
